import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { formatINRFull } from "@/lib/format";
import { Search, Filter, Plus, X, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import { LedgerTable } from "@/components/v3/LedgerTable";
import { DataFreshness } from "@/components/v3/DataFreshness";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { SourceBadge } from "@/components/v3/SourceBadge";
import { STAGE_META } from "@/components/v3/CockpitLive";
import { LoadingState, EmptyState, ErrorState } from "@/components/v3/States";

const STAGES = ["lead", "submitted", "verification", "info_required", "review", "committee", "sanctioned", "approved", "documentation_complete", "disbursed", "declined"];
const NEXT_ACTION = {
  lead: "Qualify lead", submitted: "Assign credit review", info_required: "Awaiting borrower info",
  review: "Complete credit / risk review", committee: "Committee decision", sanctioned: "Execute term sheet",
  approved: "Complete CPs & disburse", disbursed: "Servicing", declined: "—",
};
const QUEUES = [
  { key: "all", label: "All work" },
  { key: "committee", label: "Committee today", stage: "committee" },
  { key: "info", label: "Needs information", stage: "info_required" },
  { key: "aging", label: "Aging >30d", aging: 30 },
];

function StageChip({ stage, status, id }) {
  const m = STAGE_META[stage] || { label: stage, color: "#94a3b8" };
  return (
    <span data-testid={`pipeline-stage-${id}`} className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: m.color }} /> {status || m.label}
    </span>
  );
}

function Modal({ title, badge, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-slate-200 rounded-sm w-full max-w-lg max-h-[85vh] overflow-auto" data-testid="pipeline-modal">
        <div className="sticky top-0 bg-white px-5 py-3.5 border-b border-slate-200 flex items-center gap-2">
          <span className="font-semibold text-slate-900 text-sm">{title}</span>
          {badge}
          <button onClick={onClose} data-testid="modal-close-btn" className="ml-auto text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-slate-300 rounded px-3 py-2 text-sm";

function AppForm({ initial, onSubmit, busy, submitLabel }) {
  const [f, setF] = useState({
    company_Name: initial?.company_Name || "", applied_loan_amt: initial?.applied_loan_amt || "",
    loan_type: initial?.loan_type || "Secured Business Loan", stage: initial?.stage || "lead",
    owner: initial?.owner || "", collateral_type: initial?.collateral_type || "",
    collateral_value: initial?.collateral_value || "", completeness: initial?.completeness ?? 10,
    note: initial?.note || "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Company / borrower</label>
        <input data-testid="app-form-company" className={inputCls} value={f.company_Name} onChange={set("company_Name")} placeholder="e.g. Sharma Industries Pvt Ltd" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Amount (₹)</label>
          <input data-testid="app-form-amount" type="number" className={inputCls} value={f.applied_loan_amt} onChange={set("applied_loan_amt")} placeholder="50000000" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Stage</label>
          <select data-testid="app-form-stage" className={inputCls} value={f.stage} onChange={set("stage")}>
            {STAGES.map((s) => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Product</label>
          <select data-testid="app-form-product" className={inputCls} value={f.loan_type} onChange={set("loan_type")}>
            {["Secured Business Loan", "Invoice Discounting", "Corporate Loan", "Unsecured Business Loan"].map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Owner</label>
          <input data-testid="app-form-owner" className={inputCls} value={f.owner} onChange={set("owner")} placeholder="Assigned analyst" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Collateral type</label>
          <input data-testid="app-form-collateral" className={inputCls} value={f.collateral_type} onChange={set("collateral_type")} placeholder="e.g. Commercial property" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Collateral value (₹)</label>
          <input data-testid="app-form-collateral-value" type="number" className={inputCls} value={f.collateral_value} onChange={set("collateral_value")} />
        </div>
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Completeness %</label>
        <input data-testid="app-form-completeness" type="number" min="0" max="100" className={inputCls} value={f.completeness} onChange={set("completeness")} />
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Note</label>
        <textarea data-testid="app-form-note" rows={2} className={inputCls} value={f.note} onChange={set("note")} />
      </div>
      <button data-testid="app-form-submit" disabled={busy}
        onClick={() => onSubmit({
          ...f,
          applied_loan_amt: Number(f.applied_loan_amt) || 0,
          collateral_value: f.collateral_value === "" ? null : Number(f.collateral_value),
          collateral_type: f.collateral_type || null,
          completeness: Math.min(100, Math.max(0, Number(f.completeness) || 0)),
          owner: f.owner || null, note: f.note || null,
        })}
        className="bg-[#b3122a] text-white text-sm font-semibold rounded px-4 py-2 hover:bg-[#8f0e21] transition-colors disabled:opacity-50 w-full">
        {busy ? "Saving…" : submitLabel}
      </button>
    </div>
  );
}

function HistoricalView({ id, onClose }) {
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/v3/legacy/applicants/${id}`)
      .then((r) => setRec(r.data.record))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id]);
  const KEYS = ["company_Name", "applicant_no", "status", "applicant_type", "loan_type", "applied_loan_amt",
    "applied_tenor", "loan_purpose", "collateral_type", "collateral_value", "ls_loan_amount", "ls_interest_rate",
    "ls_tenor", "facility_number", "company_PAN", "GST_no", "city", "state", "source", "primary_email", "phone"];
  return (
    <Modal title="Historical application — read-only" badge={<><SourceBadge source="HISTORICAL" /><Lock size={13} className="text-slate-400" /></>} onClose={onClose}>
      {err && <ErrorState message={err} />}
      {!err && !rec && <LoadingState label="Reading from Atlas…" />}
      {rec && (
        <table className="w-full text-xs" data-testid="historical-view-table">
          <tbody>
            {KEYS.filter((k) => rec[k] !== undefined && rec[k] !== "" && rec[k] !== null).map((k) => (
              <tr key={k} className="border-b border-slate-100 last:border-0">
                <td className="py-1.5 pr-3 font-mono text-slate-400 whitespace-nowrap align-top">{k}</td>
                <td className="py-1.5 text-slate-800 break-all">{String(rec[k]).slice(0, 200)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-3 text-[11px] text-slate-400">Production record — cannot be edited. The full One Loan Workspace arrives in Build 4.</div>
    </Modal>
  );
}

export default function Pipeline3() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const stage = params.get("stage") || "";
  const aging = Number(params.get("aging") || 0);
  const q = params.get("q") || "";
  const source = params.get("source") || "all";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const pageSize = 50;
  const [counts, setCounts] = useState(null);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [countsError, setCountsError] = useState(false);
  const [pageInfo, setPageInfo] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);   // v3 row
  const [viewing, setViewing] = useState(null);   // historical row id
  const [busy, setBusy] = useState(false);
  const [refreshKey, refresh] = useLiveRefresh();

  useEffect(() => {
    const controller = new AbortController();
    api.get("/v3/pipeline/counts", { signal: controller.signal })
      .then((r) => { setCounts(r.data); setCountsError(false); })
      .catch(e => { if (e.code !== "ERR_CANCELED") { setCounts(null); setCountsError(true); } });
    return () => controller.abort();
  }, [refreshKey]);

  useEffect(() => {
    setRows(null); setErr(null);
    const controller = new AbortController();
    const t = setTimeout(() => {
      api.get("/v3/pipeline", { signal: controller.signal, params: { stage: stage || undefined, q: q || undefined, source, aging_min_days: aging || undefined, skip: (page - 1) * pageSize, limit: pageSize } })
        .then((r) => { setRows(r.data.rows); setPageInfo(r.data); })
        .catch((e) => { if (e.code !== "ERR_CANCELED") { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); setPageInfo(null); } });
    }, q ? 350 : 0);
    return () => { clearTimeout(t); controller.abort(); };
  }, [stage, q, source, aging, page, refreshKey]);

  const updateFilters = (changes, keepPage = false) => {
    const p = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => value ? p.set(key, String(value)) : p.delete(key));
    if (!keepPage) p.delete("page");
    setParams(p, { replace: !keepPage });
  };
  const setStage = (s) => updateFilters({ stage: s, aging: "" });
  const setQueue = (queue) => updateFilters({ stage: queue.stage, aging: queue.aging });
  const activeQueue = useMemo(() => {
    if (aging) return "aging";
    if (stage === "committee") return "committee";
    if (stage === "info_required") return "info";
    return stage ? "" : "all";
  }, [stage, aging]);

  const createApp = (payload) => {
    setBusy(true);
    api.post("/v3/applications", payload)
      .then((r) => { toast.success(`Test application ${r.data.application.applicant_no} created`); setCreating(false); refresh(); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setBusy(false));
  };
  const patchApp = (payload) => {
    setBusy(true);
    api.patch(`/v3/applications/${editing.id}`, payload)
      .then(() => { toast.success("Test application updated"); setEditing(null); refresh(); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setBusy(false));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1560px] financial-report" data-testid="pipeline3-page">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="ceo-label !text-[#b3122a]">Originate · Red Fort Capital Loan Pulse</div>
          <h1 className="ceo-fig text-3xl text-[#111111] mt-1">Pipeline</h1>
          <p data-testid="pipeline-source-description" className="text-[13px] text-slate-500 mt-1.5">
            Live LMS applications · read-only HISTORICAL source · HQ workflows
          </p>
        </div>
        <button data-testid="new-test-application-btn" onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 bg-[#b3122a] text-white text-sm font-semibold rounded px-3.5 py-2 hover:bg-[#8f0e21] transition-colors">
          <Plus size={15} /> New test application
        </button>
      </header>

      <DataFreshness version={refreshKey} onRefresh={refresh} prefix="pipeline" />
      {countsError && <div role="alert" data-testid="pipeline-counts-error" className="text-sm text-red-700 mb-3">Stage totals could not be refreshed.</div>}
      <div className="flex flex-wrap gap-2 pb-2 mb-4" data-testid="stage-rail">
        <button onClick={() => setStage("")} data-testid="stage-rail-all"
          className={`shrink-0 border rounded-sm px-3 py-2 text-left transition-colors ${!stage && !aging ? "border-[#b3122a] bg-[#b3122a]/5" : "border-slate-200 bg-white hover:border-slate-400"}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">All</div>
          <div data-testid="pipeline-total-count" className="text-lg font-bold tabular-nums text-slate-900 leading-tight">{counts ? counts.total : countsError ? "Unavailable" : "…"}</div>
        </button>
        {STAGES.map((s) => (
          <button key={s} onClick={() => setStage(s)} data-testid={`stage-rail-${s}`}
            className={`shrink-0 border rounded-sm px-3 py-2 text-left transition-colors ${stage === s ? "border-[#b3122a] bg-[#b3122a]/5" : "border-slate-200 bg-white hover:border-slate-400"}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider whitespace-nowrap" style={{ color: STAGE_META[s].color }}>{STAGE_META[s].label}</div>
            <div data-testid={`pipeline-stage-count-${s}`} className="text-lg font-bold tabular-nums text-slate-900 leading-tight">
              {counts ? (counts.historical[s] || 0) + (counts.v3[s] || 0) : "…"}
            </div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4" data-testid="pipeline-toolbar">
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-1.5 w-64">
          <Search size={14} className="text-slate-400 shrink-0" />
          <input data-testid="pipeline-search-input" value={q} onChange={(e) => updateFilters({ q: e.target.value })}
            placeholder="Borrower or application no." className="bg-transparent outline-none text-sm w-full min-w-0" />
        </div>
        <div className="flex items-center gap-1.5">
          <Filter size={13} className="text-slate-400" />
          {["all", "historical", "v3"].map((s) => (
            <button key={s} onClick={() => updateFilters({ source: s === "all" ? "" : s })} data-testid={`source-filter-${s}`}
              className={`text-[11px] font-bold uppercase tracking-wider rounded px-2.5 py-1.5 border transition-colors ${source === s ? "border-[#111111] bg-[#111111] text-white" : "border-slate-200 bg-white text-slate-500 hover:border-slate-400"}`}>
              {s === "v3" ? "Test·V3" : s === "historical" ? "Live LMS" : s}
            </button>
          ))}
        </div>
        <div className="h-5 w-px bg-slate-200" />
        {QUEUES.map((queue) => (
          <button key={queue.key} onClick={() => setQueue(queue)} data-testid={`queue-${queue.key}`}
            className={`text-[12px] font-semibold rounded px-2.5 py-1.5 border transition-colors ${activeQueue === queue.key ? "border-[#b3122a] text-[#b3122a] bg-[#b3122a]/5" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>
            {queue.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="pipeline-table">
        {err && <div data-testid="pipeline-load-error"><ErrorState message={String(err)} onRetry={refresh} /></div>}
        {!err && rows === null && <LoadingState label="Loading pipeline…" />}
        {!err && rows && rows.length === 0 && <EmptyState title="No applications match" detail="Try a different stage, queue or search — or create a test application." />}
        {!err && rows && rows.length > 0 && (
          <div className="overflow-x-auto">
            <LedgerTable data-testid="pipeline-ledger" labels={["Borrower", "Application no.", "Application date", "DSA / RM", "Amount", "Collateral", "Status", "Owner", "Completeness", "Score", "SLA", "Next action", "Source"]} className="w-full text-xs">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="px-3 py-2.5">Borrower</th>
                  <th className="px-3 py-2.5">Application no.</th>
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">DSA / RM</th>
                  <th className="px-3 py-2.5 text-right">Amount</th>
                  <th className="px-3 py-2.5">Collateral</th>
                  <th className="px-3 py-2.5">Stage</th>
                  <th className="px-3 py-2.5">Owner</th>
                  <th className="px-3 py-2.5 text-right">Compl.</th>
                  <th className="px-3 py-2.5 text-right">Score</th>
                  <th className="px-3 py-2.5 text-right">SLA</th>
                  <th className="px-3 py-2.5">Next action</th>
                  <th className="px-3 py-2.5">Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.record_source}-${r.id}`} data-testid={`pipeline-row-${r.loan_id}`}
                    onClick={() => navigate(`/loan/${r.record_source === "TEST_V3" ? r.id : `legacy:${r.id}`}`)}
                    tabIndex={0} onKeyDown={e => { if (e.key === "Enter") navigate(`/loan/${r.record_source === "TEST_V3" ? r.id : `legacy:${r.id}`}`); }}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                    <td data-testid={`pipeline-company-${r.loan_id}`} className="px-3 py-2.5 font-semibold text-slate-800">{r.company}</td>
                    <td data-testid={`pipeline-number-${r.loan_id}`} className="px-3 py-2.5 tabular-nums text-slate-500">{r.loan_id}</td>
                    <td data-testid={`pipeline-date-${r.loan_id}`} className="px-3 py-2.5 tabular-nums text-slate-500">{r.application_date || "Not available"}</td>
                    <td className="px-3 py-2.5 text-slate-500">{r.dsa_rm || "—"}</td>
                    <td className="px-3 py-2.5 tabular-nums text-right font-semibold text-slate-800 whitespace-nowrap" data-testid={`pipeline-amount-${r.v3_id || r.id}`}>{r.amount != null ? formatINRFull(r.amount) : "—"}</td>
                    <td className="px-3 py-2.5 text-slate-500">{r.collateral || "—"}</td>
                    <td className="px-3 py-2.5"><StageChip stage={r.stage} status={r.record_source === "HISTORICAL" ? r.status_raw : undefined} id={r.loan_id} /></td>
                    <td className="px-3 py-2.5 text-slate-600">{r.owner}</td>
                    <td className="px-3 py-2.5 tabular-nums text-right text-slate-500">{r.completeness != null ? `${r.completeness}%` : "—"}</td>
                    <td className="px-3 py-2.5 tabular-nums text-right text-slate-500">{r.score != null ? r.score : "—"}</td>
                    <td className={`px-3 py-2.5 tabular-nums text-right whitespace-nowrap ${r.age_days > 30 && !["approved", "disbursed", "declined"].includes(r.stage) ? "text-[#b3122a] font-bold" : "text-slate-500"}`}>
                      {r.age_days != null ? `${r.age_days}d` : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-slate-500 text-[12px]">{NEXT_ACTION[r.stage] || "—"}</td>
                    <td className="px-3 py-2.5"><SourceBadge source={r.record_source} /></td>
                  </tr>
                ))}
              </tbody>
            </LedgerTable>
          </div>
        )}
      </div>
      {pageInfo && !err && <nav aria-label="Application pages" data-testid="pipeline-pagination" className="flex flex-wrap items-center justify-between gap-3 py-4">
        <span data-testid="pipeline-page-summary" className="text-xs text-slate-600">{pageInfo.count ? pageInfo.skip + 1 : 0}–{pageInfo.skip + pageInfo.count} of {pageInfo.total} · newest application first</span>
        <div className="flex items-center gap-3">
          <button aria-label="Previous page" title="Previous page" data-testid="pipeline-previous-page" disabled={page <= 1 || rows === null} onClick={() => updateFilters({ page: page - 1 }, true)} className="border border-slate-300 p-2 disabled:opacity-30"><ChevronLeft size={16} /></button>
          <span data-testid="pipeline-current-page" className="text-xs">Page {page}</span>
          <button aria-label="Next page" title="Next page" data-testid="pipeline-next-page" disabled={!pageInfo.has_more || rows === null} onClick={() => updateFilters({ page: page + 1 }, true)} className="border border-slate-300 p-2 disabled:opacity-30"><ChevronRight size={16} /></button>
        </div>
      </nav>}

      {creating && (
        <Modal title="New HQ application" badge={<SourceBadge source="TEST_V3" />} onClose={() => setCreating(false)}>
          <AppForm onSubmit={createApp} busy={busy} submitLabel="Create test application" />
        </Modal>
      )}
      {editing && (
        <Modal title={`Edit ${editing.loan_id}`} badge={<SourceBadge source="TEST_V3" />} onClose={() => setEditing(null)}>
          <AppForm initial={{ company_Name: editing.company, applied_loan_amt: editing.amount, loan_type: editing.product, stage: editing.stage, owner: editing.owner, collateral_type: editing.collateral, collateral_value: editing.collateral_value, completeness: editing.completeness }}
            onSubmit={patchApp} busy={busy} submitLabel="Save changes" />
        </Modal>
      )}
      {viewing && <HistoricalView id={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
